// react
import { useCallback, useMemo, useState } from 'react';

// mui
import { Box, Button, Chip, Stack, TextField } from '@mui/material';

// components
import { BaseField, FieldProps } from '@/components/atoms/BaseField/BaseField';
import { Option } from '@/components/atoms/Select/Select';

export interface MultiSelectChipProps extends FieldProps<string> {
  className?: string;
  options: Option[];
  isCustom?: boolean;
}

const parseValueArray = (value: string | undefined): string[] => {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
};

/**
 * MultiSelectChip shows the available options as toggleable chips. When
 * isCustom is true, the user can add their own values via a small text input.
 */
export const MultiSelectChip = ({
  className,
  id,
  label,
  description,
  tooltip,
  isRequired,
  isHideTitle,
  options,
  value,
  onChange,
  isDisabled = false,
  isCustom = true,
}: MultiSelectChipProps) => {
  const [customValue, setCustomValue] = useState('');
  const [isAddAnother, setIsAddAnother] = useState(false);

  const valueArray = useMemo(() => parseValueArray(value), [value]);

  const customValues = useMemo(
    () =>
      valueArray.filter((v) => !options.find((option) => option.value === v)),
    [options, valueArray],
  );

  const toggle = useCallback(
    (v: string) => {
      const idx = valueArray.indexOf(v);
      if (idx === -1) onChange?.(JSON.stringify([...valueArray, v]));
      else {
        const next = [...valueArray];
        next.splice(idx, 1);
        onChange?.(JSON.stringify(next));
      }
    },
    [valueArray, onChange],
  );

  const handleSaveCustom = useCallback(() => {
    const trimmed = customValue.trim();
    if (!trimmed) {
      setIsAddAnother(false);
      setCustomValue('');
      return;
    }
    const matched = options.find((o) => o.label === trimmed);
    if (matched) {
      toggle(matched.value);
    } else if (!valueArray.includes(trimmed)) {
      onChange?.(JSON.stringify([...valueArray, trimmed]));
    }
    setIsAddAnother(false);
    setCustomValue('');
  }, [customValue, options, valueArray, onChange, toggle]);

  return (
    <BaseField
      id={id}
      label={label}
      description={description}
      tooltip={tooltip}
      isRequired={isRequired}
      isHideTitle={isHideTitle}
    >
      <Stack
        spacing={3}
        className={className}
        sx={{
          alignItems: 'flex-start',
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {options.map((option) => {
            const selected = valueArray.includes(option.value);
            return (
              <Chip
                key={option.value}
                label={option.label}
                onClick={isDisabled ? undefined : () => toggle(option.value)}
                color={selected ? 'primary' : 'default'}
                variant={selected ? 'filled' : 'outlined'}
                disabled={isDisabled}
              />
            );
          })}
          {customValues.map((v) => (
            <Chip
              key={v}
              label={v}
              onClick={isDisabled ? undefined : () => toggle(v)}
              onDelete={isDisabled ? undefined : () => toggle(v)}
              color='primary'
              variant='filled'
              disabled={isDisabled}
            />
          ))}
        </Box>
        {isCustom && (
          <Stack
            direction='column'
            spacing={2}
            component='form'
            onSubmit={(e: React.FormEvent) => {
              e.preventDefault();
              handleSaveCustom();
            }}
            sx={{
              alignItems: 'flex-start',
            }}
          >
            {isAddAnother && (
              <TextField
                placeholder='Add custom'
                value={customValue}
                onChange={(e) => setCustomValue(e.target.value)}
                disabled={isDisabled}
                autoFocus
                size='small'
                slotProps={{
                  htmlInput: { 'aria-label': 'add another' },
                }}
              />
            )}
            <Stack
              direction='row'
              spacing={2}
              sx={{
                alignItems: 'center',
              }}
            >
              {!isAddAnother ? (
                <Button
                  variant='outlined'
                  onClick={() => setIsAddAnother(true)}
                  disabled={isDisabled}
                >
                  Add another
                </Button>
              ) : (
                <>
                  <Button
                    variant='outlined'
                    onClick={handleSaveCustom}
                    disabled={isDisabled}
                  >
                    Save
                  </Button>
                  <Button
                    variant='text'
                    onClick={() => {
                      setIsAddAnother(false);
                      setCustomValue('');
                    }}
                  >
                    Cancel
                  </Button>
                </>
              )}
            </Stack>
          </Stack>
        )}
      </Stack>
    </BaseField>
  );
};

MultiSelectChip.displayName = 'MultiSelectChip';

export default MultiSelectChip;
